import React, { useRef, useEffect } from 'react';

const DrawerPreview: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;
    let containerRadius = 150;

    interface CollisionPoint {
      x: number;
      y: number;
      color: string;
    }

    interface Ball {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      color: string;
      collisions: number;
      collisionPoints: CollisionPoint[];
    }

    const ballColors = ['#00e5ff', '#d500f9', '#ffea00', '#ff0080', '#00ff00', '#ff9100'];
    
    const createBall = (): Ball => {
      const ballColor = ballColors[Math.floor(Math.random() * ballColors.length)];
      return {
        x: 0,
        y: 0,
        vx: (Math.random() - 0.5) * 6,
        vy: (Math.random() - 0.5) * 6,
        radius: 10,
        color: ballColor,
        collisions: 0,
        collisionPoints: []
      };
    };

    let ball = createBall();

    const resize = () => {
      if (containerRef.current && canvas) {
        const { clientWidth, clientHeight } = containerRef.current;
        width = clientWidth;
        height = clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        centerX = width / 2;
        centerY = height / 2;
        containerRadius = (Math.min(width, height) / 2) * 0.8;
      }
    };

    window.addEventListener('resize', resize);
    resize();

    const update = () => {
      ball.x += ball.vx;
      ball.y += ball.vy;

      const dist = Math.sqrt(ball.x * ball.x + ball.y * ball.y);
      if (dist + ball.radius > containerRadius) {
        const nx = ball.x / dist;
        const ny = ball.y / dist;
        
        // Find collision point on circle edge
        const collisionX = nx * containerRadius;
        const collisionY = ny * containerRadius;
        
        // Add to collision points
        ball.collisionPoints.push({ 
          x: collisionX, 
          y: collisionY, 
          color: ballColors[ball.collisions % ballColors.length] 
        });
        
        ball.collisions++;

        // Reflect velocity
        const dot = ball.vx * nx + ball.vy * ny;
        ball.vx -= 2 * dot * nx;
        ball.vy -= 2 * dot * ny;

        // Increase ball size - reach max at 50 collisions
        ball.radius += (containerRadius - 10) / 50;

        // Reset logic: after 50 collisions or if ball too big
        if (ball.collisions >= 50 || ball.radius >= containerRadius) {
          ball = createBall();
          return;
        }

        // Add random angle variation (jitter)
        const angle = Math.atan2(ball.vy, ball.vx);
        const newAngle = angle + (Math.random() - 0.5) * 0.4; // +/- ~11 degrees
        const currentSpeed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
        const targetSpeed = 6.0; 
        
        ball.vx = Math.cos(newAngle) * targetSpeed;
        ball.vy = Math.sin(newAngle) * targetSpeed;

        const overlap = dist + ball.radius - containerRadius;
        ball.x -= nx * (overlap + 1);
        ball.y -= ny * (overlap + 1);
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      
      ctx.save();
      ctx.translate(centerX, centerY);

      // Container with gradient border
      ctx.beginPath();
      ctx.arc(0, 0, containerRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fill();
      const grad = ctx.createLinearGradient(-containerRadius, -containerRadius, containerRadius, containerRadius);
      grad.addColorStop(0, '#d500f9');
      grad.addColorStop(0.5, '#ffea00');
      grad.addColorStop(1, '#00e5ff');
      ctx.strokeStyle = grad;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Drawing lines from ball to collision points
      ball.collisionPoints.forEach((point) => {
        ctx.beginPath();
        ctx.moveTo(ball.x, ball.y);
        ctx.lineTo(point.x, point.y);
        ctx.strokeStyle = point.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.6;
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      });

      // Ball
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
      ctx.fillStyle = ball.color;
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.restore();
    };

    const loop = () => {
      update();
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div ref={containerRef} className="group relative rounded-[3rem] overflow-hidden aspect-square glass-purple border-none hover:glow-purple transition-all duration-500 shadow-2xl">
      <canvas ref={canvasRef} className="block" />
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-white to-transparent pointer-events-none">
        <h3 className="text-black font-bold text-lg uppercase tracking-tight">DRAWER</h3>
        <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Geometric Trace Engine</p>
      </div>
    </div>
  );
};

export default DrawerPreview;
